// Add-weighing dialog form (Stage 1 slice 4). Plain module with no Vue and no imports, so it runs
// under `node --test`. The API checks everything again (api/core/compliance/weighings.js); these
// checks only keep "Registra pesatura" disabled and show hints.
//
// Form: { method, gross, tare, ticketNumber, net, device, weighedAt }
//   weights are the text of the number inputs; weighedAt is a datetime-local value in the
//   browser's time zone ('YYYY-MM-DDTHH:mm'). The body sends it as a UTC instant.

export const MAX_KG = 60000; // same limit as the API
export const FUTURE_TOLERANCE_MS = 5 * 60 * 1000; // same clock tolerance as the API

const METHOD_FIELDS = {
  gross_tare: ["gross", "tare", "ticketNumber"],
  direct: ["net", "device"],
};

const pad = (value) => String(value).padStart(2, "0");

// 'YYYY-MM-DDTHH:mm' in the browser's time zone, for <input type="datetime-local">.
export function localDateTime(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function emptyWeighingForm(now = new Date()) {
  return {
    method: null,
    gross: "",
    tare: "",
    ticketNumber: "",
    net: "",
    device: "",
    weighedAt: localDateTime(now),
  };
}

// Changing the method clears the other method's fields.
export function switchMethod(form, method) {
  const next = { ...form, method };
  Object.entries(METHOD_FIELDS).forEach(([other, fields]) => {
    if (other === method) return;
    fields.forEach((field) => {
      next[field] = "";
    });
  });
  return next;
}

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

// Text of a number input -> number, NaN when not a number, null when empty.
export function parseKg(value) {
  if (isBlank(value)) return null;
  if (typeof value === "number") return value;
  const text = String(value).trim().replace(",", ".");
  return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
}

// -> integer tenths of a kg, or a reason string
function tenths(value, { allowZero = false } = {}) {
  const kg = parseKg(value);
  if (kg === null) return "required";
  if (!Number.isFinite(kg)) return "invalid_type";
  const result = Math.round(kg * 10);
  if (Math.abs(kg * 10 - result) > 1e-6) return "too_precise";
  if (allowZero ? result < 0 : result <= 0) {
    return allowZero ? "negative" : "not_positive";
  }
  if (result > MAX_KG * 10) return "too_large";
  return result;
}

// The read-only net of a weighbridge weighing, or null while it cannot be computed.
export function netPreview(form) {
  const gross = tenths(form.gross);
  const tare = tenths(form.tare, { allowZero: true });
  if (typeof gross !== "number" || typeof tare !== "number") return null;
  return gross - tare > 0 ? (gross - tare) / 10 : null;
}

// { field: reason } for every problem; empty when the form can be sent. ticketRequired: false
// makes the ticket number optional (container weighings, Stage 6); vehicles always need it.
export function weighingErrors(
  form,
  now = new Date(),
  { ticketRequired = true } = {},
) {
  const errors = {};
  if (!form.method) {
    errors.method = "required";
    return errors;
  }
  const check = (field, result) => {
    if (typeof result === "string") errors[field] = result;
    return result;
  };
  const text = (field, max) => {
    if (isBlank(form[field])) errors[field] = "required";
    else if (String(form[field]).trim().length > max)
      errors[field] = "too_long";
  };
  if (form.method === "gross_tare") {
    const gross = check("gross", tenths(form.gross));
    const tare = check("tare", tenths(form.tare, { allowZero: true }));
    if (
      typeof gross === "number" &&
      typeof tare === "number" &&
      gross - tare <= 0
    ) {
      errors.tare = "net_not_positive";
    }
    if (ticketRequired || !isBlank(form.ticketNumber)) text("ticketNumber", 60);
  } else {
    check("net", tenths(form.net));
    text("device", 120);
  }
  if (isBlank(form.weighedAt)) {
    errors.weighedAt = "required";
  } else {
    const at = new Date(form.weighedAt);
    if (Number.isNaN(at.getTime())) errors.weighedAt = "invalid_date";
    else if (at.getTime() > now.getTime() + FUTURE_TOLERANCE_MS) {
      errors.weighedAt = "future_time";
    }
  }
  return errors;
}

// The POST body without revision and commandId: only the chosen method's fields.
export function buildWeighingBody(form) {
  const body = {
    method: form.method,
    weighedAt: new Date(form.weighedAt).toISOString(),
  };
  if (form.method === "gross_tare") {
    body.gross = parseKg(form.gross);
    body.tare = parseKg(form.tare);
    body.ticketNumber = String(form.ticketNumber).trim();
  } else {
    body.net = parseKg(form.net);
    body.device = String(form.device).trim();
  }
  return body;
}

const time = (value) => (value ? new Date(value).getTime() : 0);

// Newest first, the same order the API uses to pick the current weighing.
export function sortWeighings(weighings) {
  return [...(weighings || [])].sort(
    (a, b) =>
      time(b.weighedAt) - time(a.weighedAt) ||
      time(b.recordedAt) - time(a.recordedAt),
  );
}
